import {Component, Input, Output, EventEmitter, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {DialogModule} from 'primeng/dialog';
import {ButtonModule} from 'primeng/button';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import { MsfSelect } from '@components/select/select';
import {WeatherDataSourceGridStepComponent} from '@app/pages/weather/pages/gis-weather/components/weather-data-source-grid-step/weather-data-source-grid-step.component';
import { MessageService } from 'primeng/api';
import {
  ReportingService,
  WeatherDataSource,
  ExportFileType,
  ExportLayerRequest,
  ExportPointRequest
} from '@app/api/weather/services/reporting.service';

@Component({
  selector: 'app-export-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, TranslateModule, MsfSelect, WeatherDataSourceGridStepComponent],
  templateUrl: './export-dialog.component.html',
  styleUrls: ['./export-dialog.component.scss']
})
export class ExportDialogComponent {
  private translate = inject(TranslateService);
  private reportingService = inject(ReportingService);
  private messageService = inject(MessageService);

  @Input() visible: boolean = false;
  @Input() exportDataType: string = '';

  @Input() exportDataSourceOptions: any[] = [];
  @Input() exportFormat: string = '';
  @Input() selectedDataSource: string = 'era5';
  @Input() selectedDate: Date = new Date();
  @Input() exportFormatOptions = [
    {label: this.translate.instant('Shapefile'), value: 'shapefile'},
    {label: 'KML', value: 'kml'},
    {label: 'GeoJSON', value: 'geojson'},
    {label: 'Csv', value: 'csv'},
  ];
  @Input() coordinates: { latitude: number; longitude: number } | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() exportDataTypeChange = new EventEmitter<string>();
  @Output() exportFormatChange = new EventEmitter<string>();


  exportDataTypeOptions: any[] = [
    {
      names: {
        'en-US': 'Humidity',
        'ru-RU': 'Влажность',
        'uz-Latn-UZ': 'Namlik',
      },
      value: 'Humidity'
    },
    {
      names: {
        'en-US': 'Temperature Average',
        'ru-RU': 'Средняя температура',
        'uz-Latn-UZ': "O'rtacha harorat",
      },
      value: 'TemperatureAverage'
    },
    {
      names: {
        'en-US': 'Precipitation',
        'ru-RU': 'Осадки',
        'uz-Latn-UZ': "Yog'ingarchilik",
      },
      value: 'Fallout'
    },
    {
      names: {
        'en-US': 'Wind speed',
        'ru-RU': 'Скорость ветра',
        'uz-Latn-UZ': 'Shamol tezligi',
      },
      value: 'windSpeed'
    },
    {
      names: {
        'en-US': 'Solar Radiation',
        'ru-RU': 'Солнечная радиация',
        'uz-Latn-UZ': 'Quyosh nurlanishi',
      },
      value: 'SolarRadiationInfluence'
    }
  ];


  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onSaveExport(): void {
    if (!this.exportDataType || !this.exportFormat) {
      return;
    }

    let exportFileType: ExportFileType;
    switch (this.exportFormat.toLowerCase()) {
      case 'geojson':
        exportFileType = ExportFileType.GeoJSON;
        break;
      case 'kml':
        exportFileType = ExportFileType.KML;
        break;
      case 'shapefile':
        exportFileType = ExportFileType.Shapefile;
        break;

      case 'csv':
        exportFileType = ExportFileType.CSV;
        break;
      default:
        exportFileType = ExportFileType.GeoJSON;
    }

    let weatherDataSource: WeatherDataSource;
    switch (this.selectedDataSource?.toLowerCase()) {
      case 'era5':
        weatherDataSource = WeatherDataSource.Era5;
        break;
      case 'ibm':
        weatherDataSource = WeatherDataSource.IBM;
        break;
      case 'ugm':
        weatherDataSource = WeatherDataSource.UGM;
        break;
      default:
        weatherDataSource = WeatherDataSource.Era5;
    }

    if (this.coordinates && this.coordinates.latitude !== 0 && this.coordinates.longitude !==0) {
      const exportRequestWithLatLon : ExportPointRequest = {
        weatherDataSource: weatherDataSource,
        date: this.selectedDate ? this.formatDate(this.selectedDate) : this.formatDate(new Date()),
        longitude: this.coordinates.longitude,
        latitude: this.coordinates.latitude,
        exportFileType: exportFileType
      };

      this.reportingService.exportPoint({ body: exportRequestWithLatLon })
        .subscribe({
          next: this.handleExportResponse.bind(this),
          error: this.handleError.bind(this)
        });
    } else {
      const exportRequest: ExportLayerRequest = {
        weatherDataSource: weatherDataSource,
        layerName: this.exportDataType || 'Humidity',
        date: this.selectedDate ? this.formatDate(this.selectedDate) : this.formatDate(new Date()),
        exportFileType: exportFileType
      };

      this.reportingService.exportLayer({ body: exportRequest })
        .subscribe({
          next: this.handleExportResponse.bind(this),
          error: this.handleError.bind(this)
        });
    }

    this.onHide();
    this.exportDataType = '';
    this.exportFormat = '';
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private handleExportResponse(response: any): void {
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'export.zip';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    if (!/\.zip$/i.test(filename)) {
      filename = filename.replace(/\.[^/.]+$/, '');
      filename = `${filename}.zip`;
    }

    const blob = response.body;
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('SUCCESS'),
        detail: this.translate.instant('EXPORT_ADDED_TO_QUEUE')
      });
    }
  }

  private handleError(error: any): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('ERROR'),
      detail: this.translate.instant('METHOD_NOT_IMPLEMENTED')
    });
    console.error('Export failed:', error);
  }

}
